<script setup lang="ts">
   import { computed, ref, watch, reactive } from 'vue';
   import { onMounted } from '#imports';
   import { useRoute, useRouter } from 'vue-router';
   import type { PageState } from 'primevue/paginator';
   import type {
      DataTableSortEvent,
      DataTableRowExpandEvent,
   } from 'primevue/datatable';
   import DataTable from 'primevue/datatable';
   import Column from 'primevue/column';
   import Skeleton from 'primevue/skeleton';
   import Select from 'primevue/select';
   import MultiSelect from 'primevue/multiselect';
   import DatePicker from 'primevue/datepicker';
   import { formatDateString } from '~/functions/parseDateString';
   import TournamentGamesDesktop from '~/components/tournaments/TournamentGamesDesktop.vue';
   import TournamentGamesMobile from '~/components/tournaments/TournamentGamesMobile.vue';
   import type { Game } from '~/types/types';
   import { getClubName } from '~/components/tournaments/tournamentGameHelpers';
   import {
      acceptsEntries,
      entryCloseDateLabel,
      isClosedForEntry,
      isOpenForEntry,
      tournamentEntryRoute,
   } from '~/functions/tournamentEntry';
   import {
      usePublicTournaments,
      usePublicTournamentFilterOptions,
      fetchPublicTournamentGames,
   } from '~/composables/usePublicTournaments';

   const props = withDefaults(
      defineProps<{
         showCountryFilter?: boolean;
         // Optional runtime brand override. Defaults to the build-time portal brand so the
         // existing brand pages are unaffected. The embed widget passes this so a single build
         // can render any brand's tournaments (see pages/embed/tournaments.vue).
         brand?: string;
         // The entry CTA links to a same-origin route, which does not resolve correctly from
         // inside the embed iframe - the widget opts out (see pages/embed/tournaments.vue).
         showEntryButton?: boolean;
      }>(),
      {
         showCountryFilter: true,
         brand: '',
         showEntryButton: true,
      },
   );

   const route = useRoute();
   const router = useRouter();
   const config = useRuntimeConfig();
   const baseUrl = config.public.lmsApiBaseurl as string;
   const brand = props.brand || (config.public.portalBrand as string);
   const publicHeaders = {
      'X-LMS-Requester': config.public.siteDomain as string,
   };

   // Tournament expansion state
   const expandedRows = ref<Record<string, boolean>>({});
   const tournamentGames = reactive<Record<number, Game[]>>({});
   const loadingGames = reactive<Record<number, boolean>>({});
   const gameExpandedRows = reactive<Record<number, Record<string, boolean>>>(
      {},
   );
   const tableContainerRef = ref<HTMLElement | null>(null);

   // Games sort state per tournament
   const gameSortField = reactive<Record<number, string>>({});
   const gameSortOrder = reactive<Record<number, 'asc' | 'desc'>>({});

   const selectedDate = ref<Date | null>(null);
   const selectedClub = ref<string | null>(null);
   const selectedCountry = ref<string[]>([]);
   const selectedTournament = ref<string | null>(null);
   const sortField = ref<string | undefined>(undefined);
   const sortOrder = ref<'asc' | 'desc' | undefined>(undefined);
   const currentPage = ref(1);
   const isInitializing = ref(true);

   const searchFilters = computed(() => ({
      club: selectedClub.value || undefined,
      country:
         selectedCountry.value.length > 0 ? selectedCountry.value : undefined,
      tournament: selectedTournament.value || undefined,
      // active_on, not start_date: picking a single date should show what is being
      // played that day. start_date means "starting on or after", which hid the
      // tournament the user was pointing at whenever it had started earlier.
      active_on: selectedDate.value
         ? formatDateForApi(selectedDate.value)
         : undefined,
      sortBy: sortField.value as
         | 'tournament'
         | 'club'
         | 'startDate'
         | 'tournamentHandicap'
         | undefined,
      sortOrder: sortOrder.value || undefined,
      page: currentPage.value,
      perPage: 50,
   }));

   function formatDateForApi(date: Date): string {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
   }

   const { tournamentsData, isLoading, isFetching, refetch } =
      usePublicTournaments(brand, searchFilters);
   const { filterOptions } = usePublicTournamentFilterOptions(brand);

   // Parse URL params on mount and enable queries
   onMounted(() => {
      parseUrlParams();
      isInitializing.value = false;
   });

   function parseUrlParams() {
      const query = route.query;

      // Deliberately not reading the old `startDate` param. It meant "starting on or
      // after", so honouring it here would silently apply different semantics to an
      // existing link. An old link now shows an unfiltered list rather than a wrong one.
      if (query.onDate) {
         const [year, month, day] = (query.onDate as string).split('-');
         selectedDate.value = new Date(
            parseInt(year),
            parseInt(month) - 1,
            parseInt(day),
         );
      }

      if (query.club) {
         selectedClub.value = query.club as string;
      }

      if (query.country) {
         const country = query.country as string;
         selectedCountry.value = country.split(',');
      }

      if (query.tournament) {
         selectedTournament.value = query.tournament as string;
      }

      if (query.sortBy) {
         sortField.value = query.sortBy as string;
      }

      if (query.sortOrder) {
         sortOrder.value = query.sortOrder as 'asc' | 'desc';
      }

      if (query.page) {
         currentPage.value = parseInt(query.page as string);
      }
   }

   function updateUrl() {
      const query: Record<string, string | number> = {};

      if (selectedDate.value) {
         query.onDate = formatDateForApi(selectedDate.value);
      }

      if (selectedClub.value) {
         query.club = selectedClub.value;
      }

      if (selectedCountry.value.length > 0) {
         query.country = selectedCountry.value.join(',');
      }

      if (selectedTournament.value) {
         query.tournament = selectedTournament.value;
      }

      if (sortField.value) {
         query.sortBy = sortField.value;
      }

      if (sortOrder.value) {
         query.sortOrder = sortOrder.value;
      }

      if (currentPage.value > 1) {
         query.page = currentPage.value;
      }

      router.replace({ query });
   }

   watch(
      [selectedDate, selectedClub, selectedCountry, selectedTournament],
      () => {
         if (isInitializing.value) {
            return;
         }
         currentPage.value = 1;
         updateUrl();
      },
   );

   function handlePageChange(event: PageState) {
      currentPage.value = event.page + 1;
      updateUrl();
      tableContainerRef.value?.scrollIntoView({
         behavior: 'smooth',
         block: 'start',
      });
   }

   function handleDateChange() {
      if (isInitializing.value) {
         return;
      }
      currentPage.value = 1;
      updateUrl();
   }

   function handleClubChange() {
      if (isInitializing.value) {
         return;
      }
      currentPage.value = 1;
      updateUrl();
   }

   function handleCountryChange() {
      if (isInitializing.value) {
         return;
      }
      currentPage.value = 1;
      updateUrl();
   }

   function handleTournamentChange() {
      if (isInitializing.value) {
         return;
      }
      currentPage.value = 1;
      updateUrl();
   }

   function handleSort(event: DataTableSortEvent) {
      sortField.value =
         typeof event.sortField === 'string' ? event.sortField : undefined;
      sortOrder.value =
         event.sortOrder === 1
            ? 'asc'
            : event.sortOrder === -1
              ? 'desc'
              : undefined;
      currentPage.value = 1;
      updateUrl();
   }

   // The list endpoint already reports games_count per tournament, so we know before any
   // click whether the expansion panel would have anything in it. Rows with no games are
   // not expandable — otherwise the user clicks only to be told there is nothing there.
   // Note games_count can be null on the wire, and skeleton rows carry no count at all.
   function hasGames(
      row: { games_count?: number | null } | null | undefined,
   ): boolean {
      return Number(row?.games_count ?? 0) > 0;
   }

   function getRowClass(row: { games_count?: number | null }) {
      return hasGames(row) ? undefined : 'tournament-row-no-games';
   }

   async function toggleRow(row: { id: number; games_count?: number | null }) {
      if (!hasGames(row)) {
         return;
      }

      if (expandedRows.value[row.id]) {
         delete expandedRows.value[row.id];
         return;
      }

      expandedRows.value[row.id] = true;

      if (tournamentGames[row.id] || loadingGames[row.id]) {
         return;
      }

      await fetchTournamentGames(row.id);
   }

   async function handleRowClick(event: {
      data: { id: number; games_count?: number | null };
   }) {
      await toggleRow(event.data);
   }

   async function clearFilters() {
      selectedDate.value = null;
      selectedClub.value = null;
      selectedCountry.value = [];
      selectedTournament.value = null;
      currentPage.value = 1;
      updateUrl();
      await refetch();
   }

   const hasActiveFilters = computed(() => {
      return (
         selectedDate.value !== null ||
         selectedClub.value !== null ||
         selectedCountry.value.length > 0 ||
         selectedTournament.value !== null
      );
   });

   const isTableLoading = computed(() => isLoading.value || isFetching.value);

   const skeletonData = Array.from({ length: 10 }, (_, i) => ({
      id: `skeleton-${i}`,
   }));

   const tableData = computed(() => {
      if (isTableLoading.value) {
         return skeletonData;
      }
      return tournamentsData.value?.data || [];
   });

   // Brands with no tournament entry form configured get accepts_entries=false on every
   // row, so the column would render as an empty header. Only show it when at least one
   // tournament on the page is actually in the portal's entry form.
   const showEntryColumn = computed(() => {
      if (!props.showEntryButton || isTableLoading.value) {
         return false;
      }

      return (tournamentsData.value?.data || []).some(acceptsEntries);
   });

   const totalRecords = computed(() => tournamentsData.value?.meta?.total || 0);
   const rowsPerPage = computed(
      () => tournamentsData.value?.meta?.per_page || 50,
   );

   const clubOptions = computed(() => {
      const clubs = filterOptions.value?.data?.available_clubs || [];
      return clubs.map((club) => ({ label: club, value: club }));
   });

   const countryOptions = computed(() => {
      return filterOptions.value?.data?.available_countries || [];
   });

   const tournamentNameOptions = computed(() => {
      const names = filterOptions.value?.data?.available_tournaments || [];
      return names.map((name) => ({ label: name, value: name }));
   });

   const formatDate = (dateString: string) =>
      formatDateString(dateString, {
         month: 'short',
         day: 'numeric',
         year: 'numeric',
      });

   const formatDateShort = (dateString: string) =>
      formatDateString(dateString, { month: 'short', day: 'numeric' });

   // An empty result is a routine outcome for the date filter, not an edge case -
   // off-season dates and gaps between events are common - so name the date that came
   // up empty instead of leaving the user with a bare "not found".
   const emptyMessage = computed(() => {
      if (!selectedDate.value) {
         return 'No tournaments found.';
      }

      return `No tournaments running on ${formatDate(formatDateForApi(selectedDate.value))}.`;
   });

   async function fetchTournamentGames(tournamentId: number) {
      loadingGames[tournamentId] = true;

      try {
         // eslint-disable-next-line no-undef
         const params = new URLSearchParams({
            group_by_date: 'false',
         });

         if (gameSortField[tournamentId]) {
            params.append('sort_by', gameSortField[tournamentId]);
         }
         if (gameSortOrder[tournamentId]) {
            params.append('sort_order', gameSortOrder[tournamentId]);
         }

         const gamesData = await fetchPublicTournamentGames(
            baseUrl,
            tournamentId,
            publicHeaders,
            params,
         );

         let games: Game[] = [];
         if (Array.isArray(gamesData.data)) {
            games = gamesData.data;
         } else {
            const gameDates = gamesData.meta?.game_dates || [];
            for (const date of gameDates) {
               const dateGames = gamesData.data[date] || [];
               games.push(...dateGames);
            }
         }

         tournamentGames[tournamentId] = games;
         if (!gameExpandedRows[tournamentId]) {
            gameExpandedRows[tournamentId] = {};
         }
      } catch {
         tournamentGames[tournamentId] = [];
      } finally {
         loadingGames[tournamentId] = false;
      }
   }

   async function handleRowExpand(event: DataTableRowExpandEvent) {
      const tournamentId = event.data.id as number;

      if (!hasGames(event.data as { games_count?: number | null })) {
         return;
      }

      if (tournamentGames[tournamentId] || loadingGames[tournamentId]) {
         return;
      }

      await fetchTournamentGames(tournamentId);
   }

   async function handleGameSort(
      tournamentId: number,
      event: DataTableSortEvent,
   ) {
      const field =
         typeof event.sortField === 'string' ? event.sortField : undefined;

      if (!field) {
         delete gameSortField[tournamentId];
         delete gameSortOrder[tournamentId];
      } else {
         gameSortField[tournamentId] = field;
         gameSortOrder[tournamentId] = event.sortOrder === 1 ? 'asc' : 'desc';
      }

      await fetchTournamentGames(tournamentId);
   }

   const skeletonGames = Array.from({ length: 5 }, (_, i) => ({
      id: `skeleton-game-${i}`,
   })) as unknown as Game[];

   function getGamesTableData(tournamentId: number): Game[] {
      if (loadingGames[tournamentId] && !tournamentGames[tournamentId]) {
         return skeletonGames;
      }
      return tournamentGames[tournamentId] || [];
   }

   function getTournamentGames(tournamentId: number): Game[] {
      return tournamentGames[tournamentId] || [];
   }

   function isLoadingTournamentGames(tournamentId: number): boolean {
      return loadingGames[tournamentId] || false;
   }

</script>

<template>
   <div>
      <!-- Filters -->
      <div class="mt-4 rounded-lg bg-white/90 p-4 shadow-lg">
         <div
            class="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div class="flex flex-1 flex-wrap gap-4">
               <IftaLabel class="w-full sm:w-48">
                  <DatePicker
                     v-model="selectedDate"
                     placeholder="Select date"
                     class="w-full"
                     dateFormat="M d, yy"
                     showIcon
                     showButtonBar
                     @date-select="handleDateChange"
                     @clear-click="handleDateChange" />
                  <!-- Not "Start Date": the filter now matches any date a tournament
                       is running, including dates after it started. -->
                  <label>On Date</label>
               </IftaLabel>

               <IftaLabel class="w-full sm:w-64">
                  <Select
                     v-model="selectedClub"
                     :options="clubOptions"
                     optionLabel="label"
                     optionValue="value"
                     placeholder="All Clubs"
                     class="w-full"
                     showClear
                     filter
                     @change="handleClubChange" />
                  <label>Club</label>
               </IftaLabel>

               <IftaLabel class="w-full sm:w-72">
                  <Select
                     v-model="selectedTournament"
                     :options="tournamentNameOptions"
                     optionLabel="label"
                     optionValue="value"
                     placeholder="All Tournaments"
                     class="w-full"
                     showClear
                     filter
                     filterMatchMode="contains"
                     filterPlaceholder="Search tournaments..."
                     @change="handleTournamentChange" />
                  <label>Tournament</label>
               </IftaLabel>

               <IftaLabel v-if="props.showCountryFilter" class="w-full sm:w-64">
                  <MultiSelect
                     v-model="selectedCountry"
                     :options="countryOptions"
                     optionLabel="label"
                     optionValue="value"
                     placeholder="All Countries"
                     class="w-full"
                     filter
                     filterPlaceholder="Search countries"
                     @change="handleCountryChange">
                     <template #value="slotProps">
                        <div
                           v-if="slotProps.value && slotProps.value.length > 0"
                           class="flex items-center gap-1">
                           <template
                              v-for="(country, index) in slotProps.value.slice(
                                 0,
                                 2,
                              )"
                              :key="country">
                              <span>
                                 {{
                                    countryOptions.find(
                                       (c) => c.value === country,
                                    )?.flag
                                 }}
                              </span>
                              <span
                                 v-if="
                                    (index as number) <
                                    Math.min(slotProps.value.length, 2) - 1
                                 ">
                                 ,
                              </span>
                           </template>
                           <span
                              v-if="slotProps.value.length > 2"
                              class="text-gray-500">
                              +{{ slotProps.value.length - 2 }}
                           </span>
                        </div>
                        <span v-else>All Countries</span>
                     </template>
                     <template #option="slotProps">
                        <div class="flex items-center gap-2">
                           <span>{{ slotProps.option.flag }}</span>
                           <span>{{ slotProps.option.label }}</span>
                        </div>
                     </template>
                  </MultiSelect>
                  <label>Country</label>
               </IftaLabel>
            </div>

            <Button
               v-if="hasActiveFilters"
               label="Clear Filters"
               icon="pi pi-times"
               severity="secondary"
               text
               @click="clearFilters" />
         </div>
      </div>

      <!-- Data Table -->
      <div
         ref="tableContainerRef"
         class="tournament-table-wrapper mt-4 w-full rounded-md bg-white/90 p-2 shadow-lg md:p-5">
         <DataTable
            v-model:expandedRows="expandedRows"
            :value="tableData"
            :paginator="!isTableLoading && totalRecords > rowsPerPage"
            :rows="rowsPerPage"
            :totalRecords="totalRecords"
            :first="(currentPage - 1) * rowsPerPage"
            :sortField="sortField"
            :sortOrder="
               sortOrder === 'asc' ? 1 : sortOrder === 'desc' ? -1 : undefined
            "
            dataKey="id"
            :rowClass="getRowClass"
            @page="handlePageChange"
            @sort="handleSort"
            @row-expand="handleRowExpand"
            @row-click="handleRowClick"
            :lazy="true"
            removableSort
            stripedRows
            class="main-tournaments-table">
            <template #empty>
               <span data-testid="tournaments-empty">{{ emptyMessage }}</span>
            </template>

            <Column field="tournament" header="Tournament" sortable>
               <template #body="slotProps">
                  <Skeleton v-if="isTableLoading" width="80%" height="1.2rem" />
                  <div v-else>
                     <span class="font-medium text-gray-900">
                        <span v-if="slotProps.data.countryFlag" class="mr-2">
                           {{ slotProps.data.countryFlag }}
                        </span>
                        {{ slotProps.data.tournament }}
                        <span
                           v-if="slotProps.data.teams_count"
                           class="hidden text-gray-500 md:inline">
                           ({{ slotProps.data.teams_count }} teams)
                        </span>
                     </span>
                     <span
                        v-if="
                           slotProps.data.teams_count ||
                           slotProps.data.tournamentHandicap
                        "
                        class="block text-sm text-gray-500 md:hidden">
                        <span v-if="slotProps.data.teams_count">
                           {{ slotProps.data.teams_count }} teams
                        </span>
                        <span
                           v-if="
                              slotProps.data.teams_count &&
                              slotProps.data.tournamentHandicap
                           ">
                           ·
                        </span>
                        <span v-if="slotProps.data.tournamentHandicap">
                           HCP: {{ slotProps.data.tournamentHandicap }}
                        </span>
                     </span>
                  </div>
               </template>
            </Column>

            <Column
               field="club"
               header="Club"
               sortable
               class="hidden md:table-cell">
               <template #body="slotProps">
                  <Skeleton v-if="isTableLoading" width="70%" height="1.2rem" />
                  <template v-else>
                     {{ getClubName(slotProps.data.club) }}
                  </template>
               </template>
            </Column>

            <Column
               field="tournamentHandicap"
               header="Handicap"
               style="width: 140px"
               class="hidden md:table-cell">
               <template #body="slotProps">
                  <Skeleton v-if="isTableLoading" width="60%" height="1.2rem" />
                  <template v-else>
                     {{ slotProps.data.tournamentHandicap }}
                  </template>
               </template>
            </Column>

            <Column
               field="startDate"
               header="Dates"
               sortable
               class="dates-column">
               <template #body="slotProps">
                  <Skeleton v-if="isTableLoading" width="90%" height="1.2rem" />
                  <template v-else>
                     <span class="hidden whitespace-nowrap md:inline">
                        {{ formatDate(slotProps.data.startDate) }} -
                        {{ formatDate(slotProps.data.endDate) }}
                     </span>
                     <div class="flex flex-col text-xs md:hidden">
                        <span>
                           {{ formatDateShort(slotProps.data.startDate) }}
                        </span>
                        <span class="text-gray-400">
                           {{ formatDateShort(slotProps.data.endDate) }}
                        </span>
                     </div>
                  </template>
               </template>
            </Column>

            <!-- Tournament entry CTA - only for tournaments in the portal's entry form -->
            <Column v-if="showEntryColumn" header="Entry" style="width: 11rem">
               <template #body="slotProps">
                  <template v-if="isOpenForEntry(slotProps.data)">
                     <NuxtLink
                        :to="tournamentEntryRoute(slotProps.data)"
                        :data-testid="`tournament-entry-link-${slotProps.data.id}`"
                        class="block rounded-md bg-brand-secondary px-3 py-2 text-center text-sm font-semibold text-white transition-opacity hover:opacity-90"
                        @click.stop>
                        <span
                           class="hidden items-center gap-2 text-nowrap md:flex">
                           <i class="pi pi-users"></i>
                           Tournament Entry
                        </span>
                        <span
                           class="flex items-center justify-center gap-3 md:hidden">
                           <i class="pi pi-users"></i>
                           Enter
                        </span>
                     </NuxtLink>
                     <p
                        v-if="entryCloseDateLabel(slotProps.data)"
                        class="mt-1 text-center text-xs text-gray-500">
                        Closes {{ entryCloseDateLabel(slotProps.data) }}
                     </p>
                  </template>

                  <span
                     v-else-if="isClosedForEntry(slotProps.data)"
                     :data-testid="`tournament-entry-closed-${slotProps.data.id}`"
                     class="block text-center text-sm font-medium text-gray-500">
                     <span class="hidden md:inline">Registration closed</span>
                     <span class="md:hidden">Closed</span>
                  </span>
               </template>
            </Column>

            <!-- Desktop expander - omitted when the tournament has no games to show -->
            <Column
               v-if="!isTableLoading"
               style="width: 3rem"
               class="hidden md:table-cell">
               <template #body="slotProps">
                  <button
                     v-if="hasGames(slotProps.data)"
                     type="button"
                     :data-testid="`tournament-toggle-${slotProps.data.id}`"
                     :aria-expanded="Boolean(expandedRows[slotProps.data.id])"
                     :aria-label="`Show games for ${slotProps.data.tournament}`"
                     class="inline-flex size-8 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100"
                     @click.stop="toggleRow(slotProps.data)">
                     <i
                        class="pi pi-chevron-down transition-transform duration-200"
                        :class="{
                           'rotate-180': expandedRows[slotProps.data.id],
                        }"></i>
                  </button>
               </template>
            </Column>

            <!-- Mobile expand indicator - the row itself handles the toggle -->
            <Column
               v-if="!isTableLoading"
               style="width: 2rem"
               class="table-cell md:hidden">
               <template #body="slotProps">
                  <i
                     v-if="hasGames(slotProps.data)"
                     :data-testid="`tournament-chevron-mobile-${slotProps.data.id}`"
                     class="pi pi-chevron-down text-gray-400 transition-transform duration-200"
                     :class="{
                        'rotate-180': expandedRows[slotProps.data.id],
                     }"></i>
               </template>
            </Column>

            <!-- Tournament Expansion Template - Games only (no lineups/teams tab) -->
            <template #expansion="slotProps">
               <div class="p-2 md:p-0">
                  <!-- Tournament details section - mobile only -->
                  <div class="mb-4 rounded-lg bg-gray-50 p-4 md:hidden">
                     <div class="grid grid-cols-2 gap-3 text-sm">
                        <div>
                           <span class="font-medium text-gray-500">Club:</span>
                           <p class="text-gray-900">
                              {{ getClubName(slotProps.data.club) }}
                           </p>
                        </div>
                        <div>
                           <span class="font-medium text-gray-500">
                              Handicap:
                           </span>
                           <p class="text-gray-900">
                              {{ slotProps.data.tournamentHandicap }}
                           </p>
                        </div>
                        <div class="col-span-2">
                           <span class="font-medium text-gray-500">Dates:</span>
                           <p class="text-gray-900">
                              {{ formatDate(slotProps.data.startDate) }} -
                              {{ formatDate(slotProps.data.endDate) }}
                           </p>
                        </div>
                     </div>
                  </div>

                  <!-- Desktop games -->
                  <div class="hidden md:block">
                     <div
                        v-if="
                           !isLoadingTournamentGames(slotProps.data.id) &&
                           tournamentGames[slotProps.data.id] &&
                           getTournamentGames(slotProps.data.id).length === 0
                        "
                        class="py-8 text-center text-gray-500">
                        <p>No games found for this tournament.</p>
                     </div>

                     <TournamentGamesDesktop
                        v-else
                        v-model:expandedRows="
                           gameExpandedRows[slotProps.data.id]
                        "
                        :tournamentId="slotProps.data.id"
                        :games="getGamesTableData(slotProps.data.id)"
                        :isLoading="isLoadingTournamentGames(slotProps.data.id)"
                        :sortField="gameSortField[slotProps.data.id]"
                        :sortOrder="gameSortOrder[slotProps.data.id]"
                        @sort="
                           ({ field, order }) =>
                              handleGameSort(slotProps.data.id, {
                                 sortField: field,
                                 sortOrder:
                                    order === 'asc'
                                       ? 1
                                       : order === 'desc'
                                         ? -1
                                         : 0,
                              } as DataTableSortEvent)
                        " />
                  </div>

                  <!-- Mobile games -->
                  <div class="block md:hidden">
                     <div
                        v-if="
                           !isLoadingTournamentGames(slotProps.data.id) &&
                           tournamentGames[slotProps.data.id] &&
                           getTournamentGames(slotProps.data.id).length === 0
                        "
                        class="py-8 text-center text-gray-500">
                        <p>No games found for this tournament.</p>
                     </div>

                     <TournamentGamesMobile
                        v-else
                        :tournamentId="slotProps.data.id"
                        :games="getTournamentGames(slotProps.data.id)"
                        :isLoading="
                           isLoadingTournamentGames(slotProps.data.id)
                        " />
                  </div>
               </div>
            </template>
         </DataTable>
      </div>
   </div>
</template>

<style scoped>
   .tournament-table-wrapper {
      overflow: visible;
   }

   /* Below md the table scrolls inside its own container, so content wider than the
      phone cannot push the document sideways. The page-level overflow-x guard in
      main.css then has nothing to fight. */
   :deep(.main-tournaments-table .p-datatable-table-container) {
      overflow-x: auto;
   }

   :deep(.main-tournaments-table .p-datatable-thead) {
      background-color: var(--p-gray-700);
      border-bottom: 2px solid var(--p-gray-800);
   }

   /* From md up the container goes back to visible so the sticky header works -
      sticky resolves against the nearest scroll container, so it cannot stick to the
      viewport while the container scrolls. That trade only applies on phones, where a
      three-column table and a short viewport get little from a sticky header. */
   @media (min-width: 768px) {
      :deep(.main-tournaments-table),
      :deep(.main-tournaments-table .p-datatable-table-container) {
         overflow: visible !important;
      }

      :deep(.main-tournaments-table .p-datatable-thead) {
         position: sticky;
         top: var(--nav-offset, 68px);
         z-index: 10;
      }
   }

   :deep(
      .main-tournaments-table
         > .p-datatable-table-container
         > .p-datatable-table
         > .p-datatable-thead
         th
   ) {
      background-color: var(--p-gray-700);
      color: white;
   }

   :deep(.dates-column) {
      width: 70px;
   }

   @media (min-width: 768px) {
      :deep(.dates-column) {
         width: 220px;
      }
   }

   :deep(
      .main-tournaments-table
         .p-datatable-tbody
         > tr:not(.p-datatable-row-expansion)
   ) {
      cursor: pointer;
   }

   /* Rows with no games are not expandable, so they must not look clickable. */
   :deep(
      .main-tournaments-table .p-datatable-tbody > tr.tournament-row-no-games
   ) {
      cursor: default;
   }

   :deep(.p-datatable-row-expansion > td) {
      padding: 0 !important;
      padding-left: 8px !important;
   }

   @media (min-width: 768px) {
      :deep(.p-datatable-row-expansion > td) {
         padding-left: 30px !important;
      }
   }

   :deep(.nested-games-table) {
      border: 1px solid var(--p-gray-200);
      border-radius: 8px;
   }

   :deep(.nested-games-table .p-datatable-thead) {
      background-color: var(--p-gray-700) !important;
      position: sticky;
      top: calc(var(--nav-offset, 68px) + 41px);
      z-index: 5;
   }

   :deep(.nested-games-table .p-datatable-thead th) {
      background-color: var(--p-gray-700) !important;
      color: white;
      font-weight: 600;
      text-align: center;
   }

   :deep(.nested-games-table .p-datatable-thead .p-datatable-sort-icon) {
      color: white;
   }

   :deep(
      .nested-games-table
         .p-datatable-tbody
         > tr:nth-child(even):not(.p-datatable-row-expansion)
   ) {
      background-color: #fefce8;
   }

   :deep(.nested-games-table .p-datatable-row-expansion) {
      background-color: white !important;
   }

   :deep(.nested-games-table .p-datatable-row-expansion > td) {
      padding-top: 1rem !important;
      padding-bottom: 1rem !important;
   }

   :deep(.nested-games-table .p-datatable-tbody td) {
      text-align: center;
      vertical-align: middle;
   }

   :deep(
      .main-tournaments-table
         .p-datatable-tbody
         > tr:has(button[aria-expanded='true'])
   ) {
      --p-datatable-body-cell-border-color: transparent;
   }
</style>
